import { useCallback, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';

import { useAppDispatch, useAppSelector } from '@/store';
import { selectTwoFactorMaskedDestination, selectAuthStatus, selectAuthError } from '@/store/selectors/auth';
import { confirmPhoneThunk } from '@/store/thunks/auth/confirmPhoneThunk';
import { sendPhoneCodeThunk } from '@/store/thunks/auth/sendPhoneCodeThunk';
import { resendPhoneCodeThunk } from '@/store/thunks/auth/resendPhoneCodeThunk';
import { useCodeConfirmation } from '@/hooks/shared/useCodeConfirmation';
import { setRegistrationStep } from '@/utils/sessionStorage';

interface UsePhoneConfirmationState {
  code: string;
  hasSubmitted: boolean;
  isResending: boolean;
  resendCountdown: number;
  codeError: string | undefined;
  isSubmitting: boolean;
  isSubmitDisabled: boolean;
  resendDisabled: boolean;
  maskedPhone: string;
  codeLength: number;
  globalError: string | undefined;
}

interface UsePhoneConfirmationHandlers {
  handleCodeChange: (next: string) => void;
  handleSubmit: () => void;
  handleResend: () => void;
}

interface UsePhoneConfirmationReturn {
  state: UsePhoneConfirmationState;
  handlers: UsePhoneConfirmationHandlers;
}

export function usePhoneConfirmation(): UsePhoneConfirmationReturn {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const maskedDestination = useAppSelector(selectTwoFactorMaskedDestination);
  const authStatus = useAppSelector(selectAuthStatus);
  const authError = useAppSelector(selectAuthError);

  useEffect(() => {
    void dispatch(sendPhoneCodeThunk());
  }, [dispatch]);

  const handleConfirm = useCallback(
    async(code: string): Promise<boolean> => {
      const action = await dispatch(confirmPhoneThunk({ code }));
      const isFulfilled = confirmPhoneThunk.fulfilled.match(action);
      if (isFulfilled) {
        setRegistrationStep('password');
        await navigate({ to: '/registration/set-password' });
      }
      return isFulfilled;
    },
    [dispatch, navigate],
  );

  const handleResend = useCallback(async(): Promise<number | undefined> => {
    const action = await dispatch(resendPhoneCodeThunk());
    if (resendPhoneCodeThunk.fulfilled.match(action)) {
      return action.payload.expiresInSeconds;
    }
    return undefined;
  }, [dispatch]);

  const { state, handlers } = useCodeConfirmation({
    onConfirm: handleConfirm,
    onResend: handleResend,
    maskedDestination: maskedDestination ?? '',
    loading: authStatus === 'loading',
    error: authError,
  });

  return {
    state: {
      ...state,
      maskedPhone: state.maskedDestination,
    },
    handlers,
  };
}
